import { useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Hook for managing question navigation
 * @param {string} sessionId - Session ID
 * @param {Object} session - Session data
 * @param {number} currentQuestionIndex - Current question index
 * @param {Function} setCurrentQuestionIndex - Set current question index
 * @param {Object} currentAnswers - Current answers
 * @param {Object} saveTimeouts - Save timeouts state
 * @param {Function} submitAnswer - Function to submit answer
 * @returns {Object} Navigation functions and ref
 */
export const useQuestionNavigation = ({
  sessionId,
  session,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  currentAnswers,
  saveTimeouts,
  setSaveTimeouts,
  submitAnswer
}) => {
  // Ref to track if we're currently navigating (to prevent showing toast for our own navigation)
  const isNavigatingRef = useRef(false);

  // Sync question navigation to backend
  const syncQuestionIndex = useCallback(async (newIndex) => {
    try {
      logger.log(`📤 Syncing question index to database: ${newIndex}`);
      const response = await axios.patch(
        `${API_URL}/api/live-session/${sessionId}/navigate`,
        { current_question_index: newIndex },
        { withCredentials: true }
      );
      logger.log('✅ Question index synced successfully:', response.data);
    } catch (err) {
      logger.error('❌ Error syncing question navigation:', err);
      toast.error('Failed to sync navigation', { id: 'nav-error' });
    }
  }, [sessionId]);

  // Helper function to check if a question is answered
  const isQuestionAnswered = useCallback((question, answer) => {
    if (!answer) {
      logger.debug(`Question ${question.id} has no answer object`);
      return false;
    }
    
    // Check based on question type
    const questionType = question.type || question.question_type;
    
    if (questionType === 'yes_no' || questionType === 'boolean') {
      // For yes_no/boolean, check answer field
      const hasAnswer = answer.answer === 'Yes' || answer.answer === 'No' || 
                       answer.answer === true || answer.answer === false ||
                       answer.answer_boolean === true || answer.answer_boolean === false;
      logger.debug(`Question ${question.id} (${questionType}): hasAnswer=${hasAnswer}, answer=${answer.answer}`);
      return hasAnswer;
    } else if (questionType === 'numeric' || questionType === 'number') {
      // For numeric, check answer_numeric field
      const hasAnswer = answer.answer_numeric !== null && 
                       answer.answer_numeric !== undefined && 
                       answer.answer_numeric !== '' &&
                       !isNaN(answer.answer_numeric);
      logger.debug(`Question ${question.id} (${questionType}): hasAnswer=${hasAnswer}, answer_numeric=${answer.answer_numeric}`);
      return hasAnswer;
    } else {
      // Text, textarea, or other types - check answer field
      const hasAnswer = answer.answer && answer.answer.toString().trim() !== '';
      logger.debug(`Question ${question.id} (${questionType}): hasAnswer=${hasAnswer}, answer=${answer.answer}`);
      return hasAnswer;
    }
  }, []);

  // Navigation functions - Save answer before moving to next (single click)
  const goToNextQuestion = useCallback(async () => {
    if (session && session.questions && currentQuestionIndex < session.questions.length - 1) {
      const currentQuestion = session.questions[currentQuestionIndex];
      const currentAnswer = currentAnswers[currentQuestion.id];
      
      // Validate if question is answered (all questions are required in live session)
      // Check if question has required field, or validate all questions by default
      const isRequired = currentQuestion?.required !== false; // Default to true if not specified
      
      if (isRequired && !isQuestionAnswered(currentQuestion, currentAnswer)) {
        toast.error('Please answer this question before proceeding', {
          id: 'question-validation-error',
          position: 'top-center',
          duration: 3000
        });
        return;
      }
      
      // CRITICAL: Save current answer BEFORE navigating (single click solution)
      // Clear any pending timeout first to prevent double-saving
      if (saveTimeouts[currentQuestion.id]) {
        clearTimeout(saveTimeouts[currentQuestion.id]);
        // Clear from state to prevent stale timeout
        if (setSaveTimeouts) {
          setSaveTimeouts(prev => {
            const updated = { ...prev };
            delete updated[currentQuestion.id];
            return updated;
          });
        }
      }
      
      // If there's an answer, save it immediately and wait for completion
      if (currentAnswer && isQuestionAnswered(currentQuestion, currentAnswer)) {
        try {
          logger.log(`💾 Saving answer for question ${currentQuestion.id} before navigation...`);
          logger.log(`   Answer data:`, currentAnswer);
          
          // Pass the answer data directly to avoid stale closure
          await submitAnswer(currentQuestion.id, currentAnswer);
          
          logger.log(`✅ Answer saved, proceeding to next question`);
          
          // Small delay to ensure backend processes the save
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          logger.error('Error saving answer before navigation:', err);
          toast.error('Failed to save answer. Please try again.', { id: 'save-error' });
          return; // Don't navigate if save fails
        }
      } else if (isRequired) {
        // If required but not answered, show error (already handled above, but double-check)
        return;
      }
      
      // Set flag to prevent showing toast for our own navigation
      isNavigatingRef.current = true;
      
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      
      // Sync navigation to backend (this triggers real-time update for consultant)
      await syncQuestionIndex(newIndex);
      
      // Clear flag after a short delay (after real-time update processes)
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    }
  }, [session, currentQuestionIndex, currentAnswers, saveTimeouts, submitAnswer, setCurrentQuestionIndex, syncQuestionIndex, isQuestionAnswered]);

  const goToPreviousQuestion = useCallback(async () => {
    if (currentQuestionIndex > 0 && session?.questions) {
      const currentQuestion = session.questions[currentQuestionIndex];
      
      // Save current answer before navigating (if answered)
      // Clear any pending timeout first
      if (saveTimeouts[currentQuestion?.id]) {
        clearTimeout(saveTimeouts[currentQuestion.id]);
      }
      
      // If there's an answer, save it immediately
      if (currentQuestion && currentAnswers[currentQuestion.id]) {
        try {
          await submitAnswer(currentQuestion.id);
        } catch (err) {
          logger.error('Error saving answer before navigation:', err);
          // Continue navigation even if save fails (user can go back and fix)
        }
      }
      
      // Set flag to prevent showing toast for our own navigation
      isNavigatingRef.current = true;
      
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      
      // Sync navigation to backend (this triggers real-time update for consultant)
      await syncQuestionIndex(newIndex);
      
      // Clear flag after a short delay (after real-time update processes)
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    }
  }, [session, currentQuestionIndex, currentAnswers, saveTimeouts, submitAnswer, setCurrentQuestionIndex, syncQuestionIndex]);

  return {
    goToNextQuestion,
    goToPreviousQuestion,
    syncQuestionIndex,
    isNavigatingRef
  };
};

